import { X } from 'lucide-react';
import { Box, Flex, Text, chakra } from '@chakra-ui/react';
import { useModalScrollLock } from '@/hooks/useModalScrollLock';

export interface BaseModalProps {
  visible: boolean;
  onClose: () => void;
  title?: string;
  children: React.ReactNode;
  footer?: React.ReactNode;
  maxWidth?: 'sm' | 'md' | 'lg' | 'xl';
  bodyClassName?: string;
  closeOnBackdrop?: boolean;
  showCloseButton?: boolean;
  lockScroll?: boolean;
  disabled?: boolean;
}

const maxWidthMap = {
  sm: { base: '400px' },
  md: { base: '500px', md: '600px' },
  lg: { base: '500px', md: '800px', lg: '900px' },
  xl: { base: '500px', md: '900px', lg: '1100px' },
} as const;

export function BaseModal({
  visible,
  onClose,
  title,
  children,
  footer,
  maxWidth = 'lg',
  bodyClassName,
  closeOnBackdrop = true,
  showCloseButton = true,
  lockScroll = true,
  disabled = false,
}: BaseModalProps) {
  useModalScrollLock(lockScroll && visible);

  if (!visible) return null;

  const handleBackdropClick = () => {
    if (closeOnBackdrop && !disabled) {
      onClose();
    }
  };

  const handleClose = () => {
    if (!disabled) {
      onClose();
    }
  };

  return (
    <Flex
      position="fixed"
      inset="0"
      bg="rgba(0,0,0,0.5)"
      backdropFilter="blur(4px)"
      justify="center"
      alignItems="flex-start"
      zIndex={1000}
      overflowY="auto"
      p="5"
      overscrollBehavior="contain"
      onClick={handleBackdropClick}
    >
      <Flex
        bg="white"
        borderRadius="2xl"
        w="full"
        maxW={maxWidthMap[maxWidth]}
        maxH="90vh"
        boxShadow="xl"
        flexDir="column"
        my="auto"
        position="relative"
        zIndex={1001}
        onClick={(e: React.MouseEvent) => e.stopPropagation()}
      >
        {(title || showCloseButton) && (
          <Flex
            direction="row"
            justify="space-between"
            align="center"
            p={{ base: '4', md: '6' }}
            borderBottom="1px solid"
            borderColor="rgba(199,199,204,0.3)"
            flexShrink={0}
          >
            {title && (
              <Text
                as="h2"
                fontSize={{ base: 'lg', md: 'xl' }}
                fontWeight="semibold"
                color="volyx.text"
                m="0"
              >
                {title}
              </Text>
            )}
            {showCloseButton && (
              <chakra.button
                p="1.5"
                bg="transparent"
                border="none"
                cursor="pointer"
                display="flex"
                alignItems="center"
                justifyContent="center"
                borderRadius="md"
                transition="all 0.2s"
                _hover={{ bg: 'volyx.lightGray' }}
                _disabled={{ opacity: 0.5, cursor: 'not-allowed' }}
                onClick={handleClose}
                disabled={disabled}
                aria-label="Close"
              >
                <X size={24} color="#000" />
              </chakra.button>
            )}
          </Flex>
        )}

        <Box
          className={bodyClassName}
          p={{ base: '4', md: '6' }}
          overflowY="auto"
          flex="1"
          overscrollBehavior="contain"
        >
          {children}
        </Box>

        {footer && (
          <Flex
            direction="row"
            justify="flex-end"
            gap="3"
            p={{ base: '4', md: '6' }}
            borderTop="1px solid"
            borderColor="rgba(199,199,204,0.3)"
            flexShrink={0}
            bg="rgba(249,250,251,0.3)"
          >
            {footer}
          </Flex>
        )}
      </Flex>
    </Flex>
  );
}
